import React, { Component } from 'react';
import {
    DetailsList,
    DetailsListLayoutMode,
    IColumn,
    SelectionMode,
    IconButton,
    CommandButton,
    SearchBox,
    Dropdown,
    IDropdownOption,
    MessageBar,
    MessageBarType
} from '@fluentui/react';
import {
    IFeature,
    FeatureState,
    getFeatureStateLabel,
    getFeatureStateColor
} from './types';
import NoRecordsFound from '../../../../../core/components/NoRecordsFound';
import { IFeaturePermissionState } from './FeaturePermission';

interface IProps {
    features: IFeature[];
    onEditFeature: (feature: IFeature) => void;
    onManagePermissions: (feature: IFeature) => void;
    onSetStateProperty: (propertyName: keyof IFeaturePermissionState, value: any) => void;
}

interface IState {
    filteredFeatures: IFeature[];
    searchTerm: string;
    stateFilter: FeatureState | null;
    errorMessage: string;
    successMessage: string;
}

class FeatureTable extends Component<IProps, IState> {
    constructor(props: IProps) {
        super(props);

        this.state = {
            filteredFeatures: [],
            searchTerm: '',
            stateFilter: null,
            errorMessage: '',
            successMessage: ''
        };
    }

    componentDidUpdate(prevProps: Readonly<IProps>, prevState: Readonly<IState>, snapshot?: any): void {
        if (prevProps.features !== this.props.features) {
            this._applyFilters();
        }
    }

    private _onSearchChange = (event?: React.ChangeEvent<HTMLInputElement>, newValue?: string): void => {
        this.setState({ searchTerm: newValue || '' }, this._applyFilters);
    };

    private _onSearchClear = (): void => {
        this.setState({ searchTerm: '' }, this._applyFilters);
    };

    private _onStateFilterChange = (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption): void => {
        const stateFilter = option?.key !== undefined && option?.key !== '' ? (option.key as FeatureState) : null;
        this.setState({ stateFilter }, this._applyFilters);
    };

    private _applyFilters = (): void => {
        const { searchTerm, stateFilter } = this.state;
        const { features } = this.props;
        let filtered = [...features];

        // Apply search filter
        if (searchTerm.trim()) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(feature =>
                feature.title?.toLowerCase().includes(lowerSearchTerm) ||
                feature.description?.toLowerCase().includes(lowerSearchTerm)
            );
        }

        // Apply state filter
        if (stateFilter !== null) {
            filtered = filtered.filter(feature => feature.state === stateFilter);
        }

        this.setState({ filteredFeatures: filtered });
    };

    private _onAddFeature = (): void => {
        const newFeature: IFeature = {
            id: 0,
            title: '',
            state: FeatureState.Disable,
            description: '',
            modifiedAt: new Date()
        };
        this.props.onEditFeature(newFeature);
    };

    private _columns: IColumn[] = [
        {
            key: 'title',
            name: 'Feature Title',
            fieldName: 'Title',
            minWidth: 150,
            maxWidth: 250,
            isResizable: true,
            onRender: (item: IFeature) => {
                return (
                    <div style={{
                        fontWeight: 600,
                        fontSize: '14px',
                        padding: '8px 0'
                    }}>
                        {item.title}
                    </div>
                );
            }
        },
        {
            key: 'state',
            name: 'State',
            fieldName: 'State',
            minWidth: 100,
            maxWidth: 120,
            isResizable: true,
            onRender: (item: IFeature) => {
                const stateLabel = getFeatureStateLabel(item.state!);
                const stateColor = getFeatureStateColor(item.state!);
                return (
                    <div style={{
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        backgroundColor: `${stateColor}20`,
                        color: stateColor,
                        fontWeight: 600,
                        fontSize: '12px',
                        border: `1px solid ${stateColor}40`
                    }}>
                        {stateLabel}
                    </div>
                );
            }
        },
        {
            key: 'description',
            name: 'Description',
            fieldName: 'Description',
            minWidth: 200,
            maxWidth: 400,
            isResizable: true,
            isMultiline: true,
            onRender: (item: IFeature) => {
                return (
                    <div style={{
                        fontSize: '13px',
                        color: '#605e5c',
                        lineHeight: '1.4'
                    }}>
                        {item.description}
                    </div>
                );
            }
        },
        {
            key: 'modifiedAt',
            name: 'Last Modified',
            fieldName: 'ModifiedAt',
            minWidth: 120,
            maxWidth: 150,
            isResizable: true,
            onRender: (item: IFeature) => {
                const date = !!item.modifiedAt && new Date(item.modifiedAt) || new Date();
                return (
                    <div style={{ fontSize: '13px', color: '#605e5c' }}>
                        {date.toLocaleDateString()}
                    </div>
                );
            }
        },
        {
            key: 'actions',
            name: 'Actions',
            minWidth: 150,
            maxWidth: 150,
            onRender: (item: IFeature) => {
                return (
                    <div style={{ display: 'flex', gap: '4px' }}>
                        <IconButton
                            iconProps={{ iconName: 'Edit' }}
                            title="Edit Feature"
                            ariaLabel="Edit Feature"
                            onClick={() => this.props.onEditFeature(item)}
                        />
                        {item.state === FeatureState.Beta && (
                            <IconButton
                                iconProps={{ iconName: 'People' }}
                                title="Manage Permissions"
                                ariaLabel="Manage Permissions"
                                onClick={() => this.props.onManagePermissions(item)}
                                styles={{
                                    icon: { color: '#0078d4' }
                                }}
                            />
                        )}
                    </div>
                );
            }
        }
    ];

    private _renderHeaderActions = (): JSX.Element => {
        const { stateFilter } = this.state;

        const stateOptions: IDropdownOption[] = [
            { key: '', text: 'All States' },
            { key: FeatureState.Enable, text: 'Enabled' },
            { key: FeatureState.Disable, text: 'Disabled' },
            { key: FeatureState.Beta, text: 'Beta' }
        ];

        return (
            <div style={{
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #edebe9',
                padding: '12px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px'
            }}>
                <div>
                    <h3 style={{ margin: '0 0 8px 0' }}>Feature Flags Management</h3>
                    <div style={{ fontSize: '13px', color: '#605e5c' }}>
                        Manage system-wide feature flags and beta access
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <SearchBox
                        placeholder="Search features..."
                        underlined={true}
                        value={this.state.searchTerm}
                        onChange={this._onSearchChange}
                        onClear={this._onSearchClear}
                        styles={{ root: { width: '250px' } }}
                    />
                    <Dropdown
                        placeholder="Filter by state"
                        options={stateOptions}
                        selectedKey={stateFilter !== null ? stateFilter : ''}
                        onChange={this._onStateFilterChange}
                        styles={{ root: { width: '150px' } }}
                    />
                    <CommandButton
                        iconProps={{ iconName: 'Add' }}
                        text="Add Feature"
                        onClick={this._onAddFeature}
                        styles={{
                            root: { color: '#0078d4', fontWeight: 600 },
                            icon: { color: '#0078d4' }
                        }}
                    />
                </div>
            </div>
        );
    };

    render() {
        const { filteredFeatures, errorMessage, successMessage } = this.state;

        return (
            <div style={{ height: '600px', display: 'block' }}>
                {errorMessage && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        isMultiline={false}
                        onDismiss={() => this.setState({ errorMessage: '' })}
                        dismissButtonAriaLabel="Close"
                        styles={{ root: { marginBottom: 10 } }}
                    >
                        {errorMessage}
                    </MessageBar>
                )}

                {successMessage && (
                    <MessageBar
                        messageBarType={MessageBarType.success}
                        isMultiline={false}
                        onDismiss={() => this.setState({ successMessage: '' })}
                        dismissButtonAriaLabel="Close"
                        styles={{ root: { marginBottom: 10 } }}
                    >
                        {successMessage}
                    </MessageBar>
                )}

                {this._renderHeaderActions()}

                {filteredFeatures.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <NoRecordsFound />
                    </div>
                ) : (
                    <div style={{
                        height: 'calc(100% - 100px)',
                        overflow: 'auto',
                        border: '1px solid #edebe9',
                        display: 'block',
                        position: 'relative'
                    }}>
                        <DetailsList
                            items={filteredFeatures}
                            columns={this._columns}
                            selectionMode={SelectionMode.none}
                            setKey="features"
                            layoutMode={DetailsListLayoutMode.justified}
                            isHeaderVisible={true}
                            styles={{
                                root: {
                                    '.ms-DetailsHeader': {
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 100,
                                        backgroundColor: '#ffffff'
                                    }
                                }
                            }}
                        />
                    </div>
                )}
            </div>
        );
    }
}

export default FeatureTable;
